/**
 * Module dependencies.
 */

import { Lead } from '../homepage/styles';
import { Link } from 'src/components/core/links/link';
import { Svg } from 'src/components/core/svg';
import { ifProp } from 'styled-tools';
import { media } from 'src/styles/utils/media';
import { textGradient } from 'src/styles/gradients';
import { useTranslation } from 'next-i18next';
import React from 'react';
import arrowLeft from 'src/assets/svg/arrow-left.svg';
import arrowRight from 'src/assets/svg/arrow-right.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  nextUrl?: string;
  previousUrl?: string;
};

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  ${media.max.sm`
    grid-template-columns: 1fr;

    > a {
      justify-self: center;
    }
  `}
`;

/**
 * `Arrow` styled component.
 */

const Arrow = styled(Svg).attrs({ size: '48px' })`
  position: relative;
`;

/**
 * `StyledLink` styled component.
 */

const StyledLink = styled(Link)<{ arrowRight?: boolean }>`
  --gradient-link-translate-x: -1rem;

  ${textGradient}

  align-items: center;
  border: none;
  display: flex;

  ${ifProp(
    'arrowRight',
    `
    --gradient-link-translate-x: 1rem;

    justify-self: flex-end;
  `
  )}

  ${Lead} {
    margin: 0;
    padding-bottom: 8px;
  }

  &,
  ${Arrow} {
    transition: var(--transition-default);
    transition-property: background-position, color, transform;
  }

  &:hover,
  &:focus {
    transform: translateX(calc(var(--gradient-link-translate-x) / 2));

    ${Arrow} {
      color: var(--color-sage700);
      transform: translateX(var(--gradient-link-translate-x));
    }
  }
`;

/**
 * `PreviousAndNextGradientText` component.
 */

export function PreviousAndNextGradientText(props: Props) {
  const { nextUrl, previousUrl } = props;
  const { t } = useTranslation(['common']);

  if (!previousUrl && !nextUrl) {
    return null;
  }

  return (
    <Grid>
      {!previousUrl ? (
        <div />
      ) : (
        <StyledLink
          data-scroll={'fade-in-up'}
          data-scroll-offset={'20%'}
          href={previousUrl}
        >
          <Arrow icon={arrowLeft} />

          <Lead>{t('common:terms.previous')}</Lead>
        </StyledLink>
      )}

      {!!nextUrl && (
        <StyledLink
          arrowRight
          data-scroll={'fade-in-up'}
          data-scroll-offset={'20%'}
          href={nextUrl}
        >
          <Lead>{t('common:terms.next')}</Lead>

          <Arrow icon={arrowRight} />
        </StyledLink>
      )}
    </Grid>
  );
}
